import { isSkipValue, parseGradingValue } from '../../../utils/calculationHelpers';

export interface SpecGrading {
  sizeName?: string | undefined;
  grading?: string | number | null | undefined;
  measurment?: string | number | null | undefined;
  isZero?: boolean | undefined;
  isSampleSize?: boolean | undefined;
}

export interface SpecPomRow {
  gradings?: Array<SpecGrading | null | undefined> | undefined;
}

export type GradingFormat = 'sample' | 'previous' | string;

const DECIMAL_PLACES = 3;

/**
 * Rounds a derived measurement, or yields a blank when the walk produced no number.
 * @param value - computed measurement.
 * @returns the rounded measurement, or `''`.
 */
const roundMeasurement = (value: number): number | string => (Number.isNaN(value) ? '' : parseFloat(value.toFixed(DECIMAL_PLACES)));

/**
 * Reads a stored grade rule into the magnitude and direction the walk applies.
 * @param rule - the stored grade rule.
 * @param isLeftSide - true when the size sits left of the base.
 * @returns the magnitude and whether it is added.
 */
const readRule = (rule: string | number | null | undefined, isLeftSide: boolean): { value: number; shouldAdd: boolean } =>
  parseGradingValue(rule ?? '', isLeftSide);

/**
 * Locates the size a row is graded against and reads its measurement.
 * @param gradings - the row's size columns.
 * @returns the base index (-1 when nothing anchors the row) and its measurement.
 */
const resolveBase = (gradings: SpecGrading[]): { baseIndex: number; baseMeasurement: number } => {
  const baseIndex = gradings.findIndex((grading) => grading?.isZero || grading?.isSampleSize);
  if (baseIndex < 0) {
    return { baseIndex, baseMeasurement: 0 };
  }
  const stored = gradings[baseIndex]?.measurment;
  return { baseIndex, baseMeasurement: isSkipValue(stored) ? 0 : parseFloat(String(stored)) || 0 };
};

/**
 * Derives every size from the base alone — the 'sample' grading format.
 * @param gradings - the row's size columns.
 * @param baseIndex - index of the base size.
 * @param baseMeasurement - the base size's measurement.
 * @returns new size columns.
 */
const deriveFromBase = (gradings: SpecGrading[], baseIndex: number, baseMeasurement: number): SpecGrading[] =>
  gradings.map((grading, index) => {
    if (isSkipValue(grading.grading)) {
      return { ...grading, measurment: grading.grading };
    }
    if (index === baseIndex) {
      return grading.measurment === '' ? { ...grading, measurment: roundMeasurement(baseMeasurement) } : { ...grading };
    }
    const { value, shouldAdd } = readRule(grading.grading, index < baseIndex);
    return { ...grading, measurment: roundMeasurement(shouldAdd ? baseMeasurement + value : baseMeasurement - value) };
  });

/**
 * Derives every size from the one before it — the 'previous' grading format.
 *
 * The running total is not passed through `Math.abs`: a chain that legitimately crosses zero was
 * folded back to positive, reporting a larger measurement than the row holds and inverting every
 * later step of the walk. A skip token prints verbatim and takes no part in the walk, so the
 * running total carries across it to the next measured size.
 * @param gradings - the row's size columns.
 * @param baseIndex - index of the base size.
 * @param baseMeasurement - the base size's measurement.
 * @returns new size columns.
 */
const deriveFromPrevious = (gradings: SpecGrading[], baseIndex: number, baseMeasurement: number): SpecGrading[] => {
  const derived = gradings.map((grading) => ({ ...grading }));

  const walk = (from: number, to: number, step: number, isLeftSide: boolean): void => {
    let runningTotal = baseMeasurement;
    for (let index = from; isLeftSide ? index >= to : index <= to; index += step) {
      const grading = derived[index];
      if (grading && 'grading' in grading) {
        if (isSkipValue(grading.grading)) {
          derived[index] = { ...grading, measurment: grading.grading };
        } else {
          const { value, shouldAdd } = readRule(grading.grading, isLeftSide);
          runningTotal = shouldAdd ? runningTotal + value : runningTotal - value;
          derived[index] = { ...grading, measurment: roundMeasurement(runningTotal) };
        }
      }
    }
  };

  walk(baseIndex - 1, 0, -1, true);
  walk(baseIndex + 1, derived.length - 1, 1, false);
  return derived;
};

/**
 * Recomputes the measurements of every POM row from its stored grade rules.
 *
 * Kept in step with `calculatePreviousSize` / `calculationSampleSize`, which run when the user
 * edits a single cell — the spec template calls this on load and on import and those on edit, so a
 * difference between them shows up as measurements jumping the moment a cell is touched. A row
 * nothing anchors keeps the measurements it arrived with. Every row is returned as a fresh object
 * with fresh gradings, since `calculateMeasurement` edits a row in place.
 * @param rows - POM rows, each with a `gradings` array.
 * @param gradingFormat - the tenant's grading format.
 * @returns new rows; the argument is not mutated.
 */
export const calculateSpecMeasurements = <T extends SpecPomRow>(rows: T[], gradingFormat: GradingFormat): T[] =>
  (Array.isArray(rows) ? rows : []).map((row) => {
    const gradings = (row?.gradings ?? []).filter((grading): grading is SpecGrading => Boolean(grading));
    const { baseIndex, baseMeasurement } = resolveBase(gradings);
    if (baseIndex < 0) {
      return { ...row, gradings: gradings.map((grading) => ({ ...grading })) };
    }
    return {
      ...row,
      gradings:
        gradingFormat === 'sample'
          ? deriveFromBase(gradings, baseIndex, baseMeasurement)
          : deriveFromPrevious(gradings, baseIndex, baseMeasurement),
    };
  });
